import { filesize } from "filesize";
import { formatDistanceToNow } from "date-fns";

/*
 * asRelativeDate function
 * Formats a string as a relative data.
 */
export const asRelativeDate = (dateString: string): string => {
  return formatDistanceToNow(new Date(dateString), { addSuffix: true });
};

/*
 * asColor function
 * Formats a percentage as a HEX value.
 */
export const asColor = (percentage: number) => {
  if (percentage < 5) return "#DC3545";
  if (percentage < 15) return "#DC5335";
  if (percentage < 25) return "#DC7135";
  if (percentage < 35) return "#CE9321";
  if (percentage < 45) return "#D0AF00";
  if (percentage < 55) return "#C8C000";
  if (percentage < 65) return "#9EBE00";
  if (percentage < 75) return "#80B918";
  if (percentage < 85) return "#55A630";
  return "#198754";
};

/*
 * asFileSize function
 * Formats a string using the FileSize library.
 */
export const asFileSize = (number: number) => {
  return number ? filesize(number, { standard: "jedec" }) : "---";
};

/**
 * isoDateFormat function
 * Formats an ISO date string into "DD Month YYYY, time AM/PM".
 * e.g. 02 Apr 2025, 10:30 am
 */
export const isoDateFormat = (isoDateString: string): string => {
  const date = new Date(isoDateString);
  const options: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  };
  return date.toLocaleString("en-UK", options);
};

/**
 * Helper function to convert a string to title case
 * e.g. "brand narrative" => "Brand narrative"
 * @param string - The input string
 * @returns The title-cased string
 */
export const toTitleCase = (string: string): string => {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

/* Common video/ad aspect ratios, checked in order against a small tolerance for floating-point drift */
const COMMON_ASPECT_RATIOS: Array<{ label: string; value: number }> = [
  { label: "16:9", value: 16 / 9 },
  { label: "9:16", value: 9 / 16 },
  { label: "1:1", value: 1 },
  { label: "4:5", value: 4 / 5 },
  { label: "5:4", value: 5 / 4 },
  { label: "4:3", value: 4 / 3 },
  { label: "3:4", value: 3 / 4 },
];
const ASPECT_RATIO_TOLERANCE = 0.01;

/**
 * asAspectRatio function
 * Formats a raw width/height ratio as a recognisable label, e.g. 1.7778 => "16:9". Falls back to
 * a decimal ratio (e.g. "1.50:1") when it doesn't match a common video/ad format.
 *
 * @param aspectRatio - The raw width/height ratio
 * @returns The formatted ratio label, or "---" when the ratio is not yet known
 */
export const asAspectRatio = (aspectRatio: number): string => {
  if (!aspectRatio) return "---";

  const match = COMMON_ASPECT_RATIOS.find((ratio) => Math.abs(ratio.value - aspectRatio) < ASPECT_RATIO_TOLERANCE);
  if (match) return match.label;

  return `${aspectRatio.toFixed(2)}:1`;
};

/**
 * asDuration function
 * Formats a duration in seconds as M:SS, e.g. 15.07 => "0:15", 65.4 => "1:05".
 *
 * @param seconds - The duration in seconds
 * @returns The formatted M:SS string, or "---" when the duration is not yet known
 */
export const asDuration = (seconds: number): string => {
  if (!seconds) return "---";

  const totalSeconds = Math.round(seconds);
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;

  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};
